import { CompetencyMapQuery } from "@generated/graphql";
import { AccountTreeOutlined, SettingsOutlined, StorageOutlined } from "@mui/icons-material";
import { StackProps, TypographyProps } from "@mui/material";
import palette from "theme/palette";
import {
  CompetenceWithProgressInfo,
  CompetencyMapWithProgressInfo,
  KnowledgeBlockCompetenceWithProgressInfo,
  SkillWithProgressInfo,
} from "./types";

const CompetencyIcon = <AccountTreeOutlined sx={{ fontSize: 18, verticalAlign: "middle", mr: 0.5 }} />;
const KnowledgeBlockIcon = <StorageOutlined sx={{ fontSize: 18, verticalAlign: "middle", mr: 0.5 }} />;
const SkillIcon = <SettingsOutlined sx={{ fontSize: 18, verticalAlign: "middle", mr: 0.5 }} />;

type ProgressInfoListItem = {
  id: string;
  title?: {
    text: string;
    icon: JSX.Element;
    titleProps?: TypographyProps;
  };
  label: string;
  totalCount: number;
  confirmedCount: number;
  rowStackProps?: StackProps;
  firstQuestionId: string | null;
  getIdsForSearchParams: () => { competencyId: string; knowledgeBlockId?: string };
  handleClickOnLabel?: boolean;
};

export const getProgressInfoCards = (competencesWithQuestionsCount: CompetenceWithProgressInfo[] | undefined) => {
  const progressInfoCards: ProgressInfoListItem[][] = [];

  competencesWithQuestionsCount?.forEach((competency) => {
    const progressInfoList: ProgressInfoListItem[] = [];

    const {
      knowledgeBlockCompetences,
    }: { knowledgeBlockCompetences: KnowledgeBlockCompetenceWithProgressInfo[] | undefined } = competency;

    const competencyItemData: ProgressInfoListItem = {
      id: competency.id,
      title: {
        text: "Компетенция",
        icon: CompetencyIcon,
        titleProps: {
          color: palette.status.received,
        },
      },
      label: competency.title,
      totalCount: competency.confirmedQuestionsCount,
      confirmedCount: competency.questionsCount,
      rowStackProps: {
        sx: {
          ...(knowledgeBlockCompetences?.length && {
            mb: 1,
            borderBottomWidth: 2,
            borderBottomStyle: "solid",
            borderColor: palette.base[200],
          }),
        },
      },
      firstQuestionId: null,
      getIdsForSearchParams: () => ({ competencyId: competency.id }),
    };

    knowledgeBlockCompetences.forEach(
      ({
        id: knowledgeBlockId,
        knowledgeBlock,
        confirmedQuestionsCount: knowledgeBlockConfirmedQuestionsCount,
        questionsCount: knowledgeBlockQuestionsCount,
      }) => {
        const knowledgeBlockProgressInfoList: ProgressInfoListItem[] = [];

        const knowledgeBlockItemData: ProgressInfoListItem = {
          id: knowledgeBlockId,
          title: {
            text: "Блок знаний",
            icon: KnowledgeBlockIcon,
          },
          label: knowledgeBlock.title,
          totalCount: knowledgeBlockConfirmedQuestionsCount,
          confirmedCount: knowledgeBlockQuestionsCount,
          firstQuestionId: null,
          getIdsForSearchParams: () => ({ competencyId: competency.id, knowledgeBlockId: knowledgeBlock.id }),
        };

        (knowledgeBlock.skills as SkillWithProgressInfo[]).forEach((skill, skillIndex) => {
          const skillFirstQuestion = skill.questions?.[0]?.id || null;

          const skillItemData: ProgressInfoListItem = {
            id: skill.id,
            title:
              skillIndex === 0
                ? {
                    text: "Навыки",
                    icon: SkillIcon,
                  }
                : undefined,
            label: skill.title,
            totalCount: skill.confirmedQuestionsCount || 0,
            confirmedCount: skill.questions?.length || 0,
            firstQuestionId: skillFirstQuestion,
            getIdsForSearchParams: () => ({ competencyId: competency.id, knowledgeBlockId: knowledgeBlock.id }),
            handleClickOnLabel: true,
          };

          if (!knowledgeBlockItemData.firstQuestionId) {
            knowledgeBlockItemData.firstQuestionId = skillFirstQuestion;
          }
          if (!competencyItemData.firstQuestionId) {
            competencyItemData.firstQuestionId = skillFirstQuestion;
          }

          knowledgeBlockProgressInfoList.push(skillItemData);
        });

        progressInfoList.push(knowledgeBlockItemData, ...knowledgeBlockProgressInfoList);
      }
    );

    progressInfoCards.push([competencyItemData, ...progressInfoList]);
  });

  return progressInfoCards;
};

export const getCompetencyMapWithProgressInfo = (
  competencyMap: CompetencyMapQuery["competencyMap"]
): CompetencyMapWithProgressInfo | undefined => {
  if (!competencyMap?.competences) return competencyMap as CompetencyMapWithProgressInfo | undefined;

  let confirmedQuestionsCount = 0;
  let questionsCount = 0;
  let skillsCount = 0;
  let skillsWithConfirmedQuestionCount = 0;
  let emptyKnowledgeBlocksCount = 0;
  let emptyCompetencesCount = 0;

  const competences: CompetenceWithProgressInfo[] = competencyMap.competences.map((competence) => {
    let competenceConfirmedQuestionsCount = 0;
    let competenceQuestionsCount = 0;
    const knowledgeBlockCompetencesCount = competence.knowledgeBlockCompetences?.length || 0;
    if (!knowledgeBlockCompetencesCount) {
      emptyCompetencesCount += 1;
    }

    const knowledgeBlockCompetences: KnowledgeBlockCompetenceWithProgressInfo[] =
      competence.knowledgeBlockCompetences?.map((knowledgeBlockCompetence) => {
        let knowledgeBlockConfirmedQuestionsCount = 0;
        let knowledgeBlockQuestionsCount = 0;
        const knowledgeBlockSkills = knowledgeBlockCompetence.knowledgeBlock.skills || [];
        if (!knowledgeBlockSkills.length) {
          emptyKnowledgeBlocksCount += 1;
        }

        knowledgeBlockSkills.forEach((skill) => {
          const skillConfirmedQuestionsCount = skill.confirmedQuestionsCount || 0;
          skillsCount += 1;
          if (skillConfirmedQuestionsCount) {
            skillsWithConfirmedQuestionCount += 1;
          }
          knowledgeBlockConfirmedQuestionsCount += skillConfirmedQuestionsCount;
          knowledgeBlockQuestionsCount += skill.questions?.length || 0;
          return skill;
        });

        competenceConfirmedQuestionsCount += knowledgeBlockConfirmedQuestionsCount;
        competenceQuestionsCount += knowledgeBlockQuestionsCount;

        return {
          ...knowledgeBlockCompetence,
          confirmedQuestionsCount: knowledgeBlockConfirmedQuestionsCount,
          questionsCount: knowledgeBlockQuestionsCount,
          knowledgeBlock: {
            ...knowledgeBlockCompetence.knowledgeBlock,
            skills: knowledgeBlockSkills,
          },
        };
      }) || [];

    confirmedQuestionsCount += competenceConfirmedQuestionsCount;
    questionsCount += competenceQuestionsCount;

    return {
      ...competence,
      confirmedQuestionsCount: competenceConfirmedQuestionsCount,
      questionsCount: competenceQuestionsCount,
      knowledgeBlockCompetences,
    };
  });

  return {
    ...competencyMap,
    competences,
    confirmedQuestionsCount,
    questionsCount,
    progressPercent:
      skillsCount + emptyKnowledgeBlocksCount + emptyCompetencesCount
        ? Math.round(
            (skillsWithConfirmedQuestionCount / (skillsCount + emptyKnowledgeBlocksCount + emptyCompetencesCount)) * 100
          )
        : 0,
  };
};
